import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight } from '../../components/LegalPageLayout';

const TermsOfService = () => {
    return (
        <LegalPageLayout title="Terms of Service" lastUpdated="March 2026">
            <Section title="1. Platform Purpose">
                <Paragraph>
                    ClientCheck is a professional platform designed to help contractors and service providers 
                    document job experiences in a structured and professional manner. The platform enables 
                    contractors to share factual information about their experiences at job addresses, helping 
                    the contractor community make better-informed business decisions.
                </Paragraph>
                <Paragraph>
                    By using ClientCheck, you acknowledge that:
                </Paragraph>
                <List items={[
                    "The platform is intended for contractors and service providers",
                    "Reviews describe job experiences at specific addresses, not personal attacks on individuals",
                    "The platform promotes professional documentation rather than public accusations",
                    "All content should be factual, job-related, and professionally written"
                ]} />
            </Section>

            <Section title="2. User Responsibilities">
                <Paragraph>
                    As a user of ClientCheck, you agree to the following responsibilities:
                </Paragraph>
                <List items={[
                    "Provide truthful and accurate information in your profile and reviews",
                    "Only submit reviews for jobs you were personally involved in",
                    "Avoid insults, threats, personal attacks, or unfounded accusations",
                    "Follow all platform guidelines for factual reporting",
                    "Maintain professional language and tone in all submissions",
                    "Keep your account credentials secure and confidential"
                ]} />
                <Highlight>
                    Users are solely responsible for the content they submit to ClientCheck. 
                    You represent that all information you provide is accurate and truthful to 
                    the best of your knowledge.
                </Highlight>
            </Section>

            <Section title="3. Content Restrictions">
                <Paragraph>
                    To maintain a professional and legally compliant platform, the following types of 
                    content are strictly prohibited:
                </Paragraph>
                <List items={[
                    "Harassment, threats, or intimidation of any kind",
                    "Profanity, vulgar language, or offensive content",
                    "Criminal accusations or allegations of illegal activity",
                    "Defamatory statements or content that damages reputation without factual basis",
                    "Discriminatory language based on race, gender, religion, or other protected characteristics",
                    "Personal private information such as phone numbers, email addresses, or home addresses of individuals",
                    "Content that identifies specific individuals by name",
                    "False or misleading information submitted with intent to deceive"
                ]} />
                <Paragraph>
                    Submissions may be automatically blocked by our content filtering system if they 
                    contain prohibited language or patterns. Reviews that violate these restrictions 
                    may be removed without notice.
                </Paragraph>
            </Section>

            <Section title="4. Moderation Rights">
                <Paragraph>
                    ClientCheck reserves the right to moderate platform content to maintain community 
                    standards and legal compliance. This includes the right to:
                </Paragraph>
                <List items={[
                    "Review any submitted content before or after publication",
                    "Remove, edit, or hide reviews that violate platform policies",
                    "Suspend or terminate user accounts that repeatedly violate guidelines",
                    "Restrict access to platform features for users who abuse the system",
                    "Archive reviews when property ownership changes occur",
                    "Implement automated content filtering systems"
                ]} />
                <Paragraph>
                    Moderation decisions are made at ClientCheck's sole discretion and are final. 
                    We are not obligated to provide detailed explanations for moderation actions, 
                    though we strive to maintain transparency and fairness.
                </Paragraph>
            </Section>

            <Section title="5. Address-Based Reporting">
                <Paragraph>
                    ClientCheck organizes job experience reports around job addresses rather than 
                    personal identities. This approach:
                </Paragraph>
                <List items={[
                    "Protects privacy by not publicly identifying individuals",
                    "Allows contractors to share experiences about specific job locations",
                    "Provides useful information for contractors considering work at an address",
                    "Supports professional documentation without enabling personal attacks"
                ]} />
                <Paragraph>
                    Risk ratings and scores are calculated based on aggregate review data for each 
                    address and do not represent judgments about specific individuals.
                </Paragraph>
            </Section>

            <Section title="6. Property Ownership Changes">
                <Paragraph>
                    ClientCheck acknowledges that property ownership changes over time. When a 
                    property changes ownership:
                </Paragraph>
                <List items={[
                    "Previous reviews may be archived and excluded from current experience calculations",
                    "Address experience scores may be reset to reflect the new ownership",
                    "Historical records may be retained internally for moderation and compliance purposes",
                    "New owners are not held accountable for previous ownership job history"
                ]} />
                <Paragraph>
                    Property ownership resets may be requested through our administrative process 
                    with appropriate documentation.
                </Paragraph>
            </Section>

            <Section title="7. Limitation of Liability">
                <Paragraph>
                    ClientCheck provides a platform for structured job experience documentation. 
                    Important limitations include:
                </Paragraph>
                <List items={[
                    "ClientCheck does not verify or guarantee the accuracy of user-submitted content",
                    "Users rely on platform information at their own discretion",
                    "ClientCheck is not responsible for business decisions made based on platform data",
                    "The platform provides documentation tools, not legal or financial advice",
                    "Risk scores and ratings are algorithmic calculations, not endorsements or warnings"
                ]} />
                <Highlight>
                    ClientCheck is provided "as is" without warranties of any kind. We are not 
                    liable for any damages arising from your use of the platform or reliance on 
                    information contained within it.
                </Highlight>
            </Section>

            <Section title="8. Account Termination">
                <Paragraph>
                    ClientCheck may suspend or terminate user accounts for:
                </Paragraph>
                <List items={[
                    "Violations of these Terms of Service or other platform policies",
                    "Repeated submission of prohibited content",
                    "Abuse of platform features or other users",
                    "Attempts to manipulate scoring systems or bypass moderation",
                    "Providing false information during registration",
                    "Any activity that threatens platform integrity or user safety"
                ]} />
                <Paragraph>
                    Account termination may result in loss of access to your submitted content and 
                    platform features. Terminated accounts may be ineligible for reinstatement.
                </Paragraph>
            </Section>

            <Section title="9. Policy Updates">
                <Paragraph>
                    ClientCheck may update these Terms of Service as the platform evolves. Significant 
                    changes will be communicated through:
                </Paragraph>
                <List items={[
                    "Updates to the 'Last Updated' date on this page",
                    "Notifications within the platform for logged-in users",
                    "Email communications for material changes"
                ]} />
                <Paragraph>
                    Continued use of ClientCheck after policy updates constitutes acceptance of the 
                    revised terms.
                </Paragraph>
            </Section>

            <Section title="10. Contact">
                <Paragraph>
                    For questions about these Terms of Service, please contact our support team 
                    through the Contact page on our platform.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default TermsOfService;
